import React, { useEffect, useRef } from 'react'
import { Form } from 'react-bootstrap'
import PropTypes from 'prop-types'
import RBDateRangePicker from 'react-bootstrap-daterangepicker'
import 'bootstrap-daterangepicker/daterangepicker.css'
import classNames from 'classnames'
import {
  formatDate as formatDateUtil,
  getCurrentMoment,
  isValidMomentDate,
} from '../../utils/date'

const SimpleDatePicker = ({
  label,
  labelProps,
  className,
  value,
  onChange,
  disabled = false,
  placeholder = 'MM/DD/YYYY',
  position = 'down',
  opens = 'right',
  inputStyle = {
    border: '1px solid #000',
    background: '#fff',
  },
  ...rest
}) => {
  const picker = useRef(null)

  useEffect(() => {
    if (picker.current) {
      if (value) {
        picker.current.setStartDate(formatDateUtil(value))
        picker.current.setEndDate(formatDateUtil(value))
        picker.current.$picker.val(formatDateUtil(value))
      } else {
        picker.current.$picker.val('')
        const currentMoment = getCurrentMoment()
        picker.current.setStartDate(formatDateUtil(currentMoment))
        picker.current.setEndDate(formatDateUtil(currentMoment))
      }
    }
  }, [value, picker])

  // Add scroll event listener to close picker when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (picker.current && picker.current.$picker.data('daterangepicker')) {
        const daterangepicker = picker.current.$picker.data('daterangepicker')
        if (daterangepicker && daterangepicker.isShowing) {
          daterangepicker.hide()
        }
      }
    }

    // Add scroll event listeners to window and any scrollable containers
    window.addEventListener('scroll', handleScroll, true)
    document.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [])

  const renderLabel = () => {
    if (label) {
      return (
        <Form.Label className='text-uppercase' {...labelProps}>
          {label}
        </Form.Label>
      )
    }
    return null
  }

  const onApply = (e, datePicker) => {
    if (datePicker.startDate && isValidMomentDate(datePicker.startDate)) {
      const formattedDate = formatDateUtil(datePicker.startDate)
      onChange(formattedDate)
      datePicker.element.val(formattedDate)
    } else {
      datePicker.element.val('')
      onChange('')
    }
  }

  const handleChange = (e) => {
    if (isValidMomentDate(e.target.value)) {
      onChange(e.target.value)
    }
  }

  const initialDatePickerSettings = {
    singleDatePicker: true,
    showDropdowns: true,
    autoUpdateInput: false,
    locale: {
      format: 'MM/DD/YYYY',
    },
    applyButtonClasses: 'btn-saffron font-montserrat',
    cancelButtonClasses: 'btn-black font-montserrat',
    drops: position,
    opens: opens,
    autoApply: true,
  }

  return (
    <span className='d-flex position-relative align-items-center'>
      <div className='d-lg-flex align-items-center w-100'>
        {renderLabel()}
        <RBDateRangePicker
          ref={picker}
          onApply={onApply}
          initialSettings={initialDatePickerSettings}>
          <input
            autoComplete={'off'}
            placeholder={placeholder}
            disabled={disabled}
            type='text'
            value={value || ''}
            className={classNames('form-control', className)}
            onChange={handleChange}
            style={inputStyle}
            {...rest}
          />
        </RBDateRangePicker>
      </div>
    </span>
  )
}

SimpleDatePicker.propTypes = {
  label: PropTypes.string,
  labelProps: PropTypes.object,
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  position: PropTypes.oneOf(['down', 'up', 'auto']),
  opens: PropTypes.oneOf(['left', 'right', 'center']),
  inputStyle: PropTypes.object,
}

export default SimpleDatePicker
